import { type FC, useState } from 'react';
import { CompletedTrainingCard } from './components/CompletedTrainingCard';
import styled from 'styled-components';
import { VoluntaryTrainingCard } from './components/VoluntaryTrainingCard';
import { MandatoryTrainingCard } from './components/MandatoryTrainingCard';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import {
  type EnrolledCampaignListResponse,
  useCampaignsQuery,
  type VoluntaryItem,
  type VoluntaryTrainingListResponse,
} from '../../apiHooks/useCampaignsQuery';
import { RTLFixSpan } from './utils/RTLFixSpan';
import { NoMandatoryTrainingsCard } from './components/NoMandatoryTrainingsCard';
import { useCyberIqSettingsQuery } from '../../apiHooks/useCyberIqSettingsQuery';
import { useMeQuery } from '../../apiHooks/useMeQuery';
import { Icon, IconType } from '../Props/Icon/Icon';

export const TrainingWidget: FC = () => {
  const {
    data: {
      completed: completedCampaigns,
      mandatory: mandatoryCampaigns,
      voluntary: voluntary,
      voluntaryModules: voluntaryCampaigns,
    },
    refetch,
  } = useCampaignsQuery(true);
  const { data: meData } = useMeQuery();
  const { data: cyberIqSettings } = useCyberIqSettingsQuery(meData?.learnerId ?? '');
  const isVoluntaryTrainingsActive: boolean = cyberIqSettings?.voluntaryTraining || false;
  const voluntaryTrainings: VoluntaryItem[] = isVoluntaryTrainingsActive ? [...voluntary, ...voluntaryCampaigns] : [];

  const [isVoluntaryOpen, setIsVoluntaryOpen] = useState(false);
  const [isCompletedOpen, setIsCompletedOpen] = useState(false);

  const handleCampaignComplete = () => {
    refetch();
  };

  return (
    <CardContainer>
      <Header>
        <HeaderLeft>
          <IconBadge>
            <Icon icon={IconType.GRADUATE_CAP} width={16} height={16} fill="transparent" />
          </IconBadge>
          <Heading>
            <NamedContentEntryText container={'h2'} refKey="sectionTitles" subKey="yourTraining" />
            <Subtitle>
              <Icon icon={IconType.SPARKLES} width={10} height={10} fill="none" />
              <span>Assigned learning · Command Center</span>
            </Subtitle>
          </Heading>
        </HeaderLeft>
        <CountBadge>
          <RTLFixSpan>{mandatoryCampaigns.length}</RTLFixSpan> <span>Active</span>
        </CountBadge>
      </Header>

      <ActiveList>
        {mandatoryCampaigns.length ? (
          mandatoryCampaigns.map((campaign: EnrolledCampaignListResponse) => (
            <MandatoryTrainingCard key={campaign.id} campaign={campaign} />
          ))
        ) : (
          <NoMandatoryTrainingsCard />
        )}
      </ActiveList>

      {isVoluntaryTrainingsActive && (
        <Section>
          <SectionToggle onClick={() => setIsVoluntaryOpen((open: boolean) => !open)}>
            <Chevron $open={isVoluntaryOpen}>
              <Icon icon={IconType.CHEVRON_DOWN} width={14} height={14} />
            </Chevron>
            <NamedContentEntryText container={'span'} refKey="yourTraining" subKey="voluntary" />
            <Count>
              (<RTLFixSpan>{voluntaryTrainings.length}</RTLFixSpan>)
            </Count>
          </SectionToggle>
          {isVoluntaryOpen &&
            (voluntaryTrainings.length ? (
              voluntaryTrainings.map((campaign: VoluntaryTrainingListResponse | EnrolledCampaignListResponse) => (
                <VoluntaryTrainingCard key={campaign.id} campaign={campaign} onComplete={handleCampaignComplete} />
              ))
            ) : (
              <NoTrainingsText>
                <NamedContentEntryText container={'span'} refKey={'yourTraining'} subKey={'noVoluntaryTrainings'} />
              </NoTrainingsText>
            ))}
        </Section>
      )}

      <Section>
        <SectionToggle onClick={() => setIsCompletedOpen((open: boolean) => !open)}>
          <Chevron $open={isCompletedOpen}>
            <Icon icon={IconType.CHEVRON_DOWN} width={14} height={14} />
          </Chevron>
          <NamedContentEntryText container={'span'} refKey="yourTraining" subKey="completed" />
          <Count>
            (<RTLFixSpan>{completedCampaigns.length}</RTLFixSpan>)
          </Count>
        </SectionToggle>
        {isCompletedOpen &&
          (completedCampaigns.length ? (
            completedCampaigns.map((campaign: EnrolledCampaignListResponse) => (
              <CompletedTrainingCard
                key={campaign.id}
                campaignUrl={campaign.campaignUrl}
                dueDate={campaign.dueDate}
                name={campaign.name}
              />
            ))
          ) : (
            <NoTrainingsText>
              <NamedContentEntryText container={'span'} refKey={'yourTraining'} subKey={'noCompletedTrainings'} />
            </NoTrainingsText>
          ))}
      </Section>
    </CardContainer>
  );
};

const CardContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.prototype.card};
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.card};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const IconBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.prototype.primary.replace(')', ' / 10%)')};
  color: ${({ theme }) => theme.colors.prototype.userPrimaryText};

  & svg {
    fill: none;
  }
`;

const Heading = styled.div`
  min-width: 0;

  h2 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: 0.42px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.prototype.foreground};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Subtitle = styled.p`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 2px 0 0;
  font-size: 10px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};

  svg {
    flex-shrink: 0;
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 9999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${({ theme }) => theme.colors.prototype.primary.replace(')', ' / 10%)')};
  color: ${({ theme }) => theme.colors.prototype.userPrimaryText};

  & > :last-child {
    margin-left: 4px;
  }
`;

const ActiveList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 10px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionToggle = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;

const Chevron = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$open',
}) <{ $open: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
`;

const NoTrainingsText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
  font-weight: 400;
  text-align: center;
`;

const Count = styled.div`
  margin-left: 4px;
`;
